import { useState } from "react";
import gql from "graphql-tag";
import { useSearchParams } from "react-router-dom";

import { mapToKnownNablaRegionOrNull } from "auth/utils";
import { Button } from "components/Button/Button";
import { Submit } from "components/Button/Submit";
import { Form } from "components/Form/Form/Form";
import { FormInput } from "components/Form/Input/FormInput";
import { PublicScreenWrapper } from "components/PublicScreenWrapper/PublicScreenWrapper";
import {
  RegionForEmail,
  StartAccountPasswordReset,
} from "generated/unauth-account";
import { useLazyQuery } from "graphql-client/useLazyQuery";
import { useMutation } from "graphql-client/useMutation";
import { useGoBack } from "hooks";
import { useIsDesktop } from "hooks/useMediaQuery";
import { useTranslation } from "i18n";
import { routes } from "routes";
import { notifier } from "utils/notifier";

gql`
  # schema = UNAUTHENTICATED_ACCOUNT
  mutation StartAccountPasswordReset($email: String!) {
    startUserInitiatedAccountPasswordReset(email: $email) {
      _
    }
  }
`;

export const ForgotPassword = () => {
  const t = useTranslation();
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const goBack = useGoBack(false);

  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);
  const [fetchRegionForEmail] = useLazyQuery(RegionForEmail, {
    context: {
      requestContext: {
        region: "CLOSEST_REGION",
      },
    },
  });
  const [startAccountPasswordReset] = useMutation(StartAccountPasswordReset);

  return (
    <PublicScreenWrapper>
      {showConfirmationScreen ? (
        <div className="flex-col w-full max-w-400">
          <h1 className="title mb-14">
            {t("forgot_password.confirmation.title")}
          </h1>
          <div className="text-16 text-center mb-40 leading-normal">
            {t("forgot_password.confirmation.subtitle")}
          </div>

          <Button
            large
            secondary
            to={routes.LOGIN}
            label={t("forgot_password.back_to_login")}
          />
        </div>
      ) : (
        <Form<{ email: string }>
          className="flex-col w-full max-w-400"
          initialValues={{ email: searchParams.get("email") ?? "" }}
          validationSchema={{ email: "requiredEmail" }}
          onSubmit={async ({ email }) => {
            try {
              const regionForEmailResult = await fetchRegionForEmail({
                variables: { email },
              });
              if (!regionForEmailResult.data?.regionForEmail?.region) {
                throw new Error("No nabla region found.");
              }
              const requestRegion = mapToKnownNablaRegionOrNull(
                regionForEmailResult.data.regionForEmail.region,
              );
              if (!requestRegion) {
                throw new Error("Nabla region not supported.");
              }

              const data = await startAccountPasswordReset(
                { email },
                { requestContext: { region: requestRegion } },
              );
              if (data) setShowConfirmationScreen(true);
            } catch (e) {
              console.error(e);
              notifier.error({
                user: t("choose_credentials.reset_password_failed"),
              });
            }
          }}
        >
          <h1 className="title mb-16">{t("forgot_password.title")}</h1>
          <div className="text-16 text-center mb-40 leading-normal">
            {t("forgot_password.subtitle")}
          </div>

          <FormInput
            autoFocus={isDesktop}
            name="email"
            type="email"
            label={t("forgot_password.forgot_password.email_address")}
            placeholder={t("forgot_password.email_placeholder")}
          />

          <Submit
            className="mt-24"
            large
            label={t("forgot_password.forgot_password.reinitialise_password")}
          />

          <button
            className="mt-18 text-primary text-center hover:opacity-70"
            onClick={() => goBack(routes.LOGIN)}
          >
            {t("forgot_password.back_to_login")}
          </button>
        </Form>
      )}
    </PublicScreenWrapper>
  );
};
